import { Alert, Box, Button, Card, CardContent, InputAdornment, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import type { JSX } from "react";

import type { BudgetKey, Budgets, SetBudgetsRequest } from "../types.js";
import { formatUsd } from "./format.js";

const ROWS: { key: BudgetKey; label: string; help: string }[] = [
  {
    key: "daily_sales",
    label: "Daily — sales AI",
    help: "Shared by the Lead agent and the Harvester. When today's spend reaches it, both stop until the daily reset (09:00 Riga).",
  },
  {
    key: "daily_marketing",
    label: "Daily — marketing AI",
    help: "The marketing agent's own cap; it stops for the day when reached.",
  },
  {
    key: "monthly",
    label: "Monthly — all AI",
    help: "Shown against this month's spend below and flagged when crossed. Not a hard stop — that would also switch off support chat and Otto. 0 hides the bar.",
  },
];

/**
 * System Admin ▸ AI Providers ▸ Budgets (2026-09-25). The numbers live in the
 * AI gateway; the agents read their daily cap from it, so a change here takes
 * effect within about a minute, with no deploy. "Use default" clears a value
 * back to the gateway's setting.
 */
export function BudgetsCard({
  budgets,
  error,
  busy,
  onSave,
}: {
  budgets: Budgets | null;
  error: string | null;
  busy: boolean;
  onSave: (body: SetBudgetsRequest) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Record<BudgetKey, string>>({
    daily_sales: "",
    daily_marketing: "",
    monthly: "",
  });

  useEffect(() => {
    if (!budgets) return;
    setDraft({
      daily_sales: String(budgets.daily_sales.usd),
      daily_marketing: String(budgets.daily_marketing.usd),
      monthly: String(budgets.monthly.usd),
    });
  }, [budgets]);

  const parsed = (k: BudgetKey): number | null => {
    const n = Number(draft[k]);
    return draft[k].trim() !== "" && Number.isFinite(n) && n >= 0 && n <= 100_000 ? n : null;
  };
  const changes: SetBudgetsRequest = {};
  if (budgets) {
    for (const { key } of ROWS) {
      const n = parsed(key);
      if (n !== null && n !== budgets[key].usd) changes[key] = n;
    }
  }
  const invalid = ROWS.some(({ key }) => parsed(key) === null);
  const dirty = Object.keys(changes).length > 0;

  return (
    <Card data-testid="budgets-card">
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          Budgets
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : null}
        {!budgets ? (
          <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
            {error ? "" : "Loading…"}
          </Typography>
        ) : (
          <Box sx={{ mt: 1.5, display: "grid", gap: 2 }}>
            {ROWS.map(({ key, label, help }) => (
              <Box
                key={key}
                sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", sm: "200px 160px 1fr" }, alignItems: "start" }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 500 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                    {budgets[key].source === "dashboard"
                      ? `Set here · default ${formatUsd(budgets[key].default_usd)}`
                      : "Default"}
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  type="number"
                  value={draft[key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  error={parsed(key) === null}
                  inputProps={{ min: 0, max: 100000, step: 1, "aria-label": label }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  disabled={busy}
                />
                <Box>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{help}</Typography>
                  {budgets[key].source === "dashboard" ? (
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() => onSave({ [key]: null })}
                      sx={{ minWidth: 0, p: 0, mt: 0.5, fontSize: 12 }}
                    >
                      Use default ({formatUsd(budgets[key].default_usd)})
                    </Button>
                  ) : null}
                </Box>
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" size="small" disabled={busy || invalid || !dirty} onClick={() => onSave(changes)}>
                Save budgets
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
